import { PayrollSystem, payrollSystemMapping } from '$constants'

type VariantOption = {
  label: string
  key: string
}

type PayrollVariantConfig = {
  options: VariantOption[]
  /** Maps sorted comma-joined active keys to a PayrollSystem enum value */
  compositions: Record<string, PayrollSystem>
}

/**
 * Config-driven variant definitions per base payroll system.
 *
 * To add a new variant to an existing base:
 *  1. Add a new PayrollSystem enum value in the API SDK (e.g. AcePayrollRules)
 *  2. Add the option to the `options` array
 *  3. Add every possible key combination to `compositions` (keys sorted alphabetically, comma-joined)
 *
 * Example — adding "Export with Adjustments" to AcePayroll (which already has style50):
 *
 *   [PayrollSystem.AcePayroll]: {
 *     options: [
 *       { label: 'Export using style #50', key: 'style50' },
 *       { label: 'Export with Adjustments', key: 'rules' },
 *     ],
 *     compositions: {
 *       'style50': PayrollSystem.AcePayroll50,          // only style50 checked
 *       'rules': PayrollSystem.AcePayrollRules,         // only rules checked
 *       'rules,style50': PayrollSystem.AcePayroll50Rules // both checked (keys sorted)
 *     },
 *   },
 */
export const payrollVariants: Partial<
  Record<PayrollSystem, PayrollVariantConfig>
> = {
  [PayrollSystem.AcePayroll]: {
    options: [{ label: 'Export using style #50', key: 'style50' }],
    compositions: { style50: PayrollSystem.AcePayroll50 },
  },
  [PayrollSystem.IPayroll]: {
    options: [{ label: 'Export with Adjustments', key: 'rules' }],
    compositions: { rules: PayrollSystem.IPayrollRules },
  },
  [PayrollSystem.PayHero]: {
    options: [{ label: 'Export with Adjustments', key: 'rules' }],
    compositions: { rules: PayrollSystem.PayHeroRules },
  },
  [PayrollSystem.Smartly]: {
    options: [{ label: 'Export with Adjustments', key: 'rules' }],
    compositions: { rules: PayrollSystem.SmartlyRules },
  },
}

/** Reverse lookup: maps a composed variant enum value back to its base + active keys */
const variantLookup = new Map<
  PayrollSystem,
  { base: PayrollSystem; activeKeys: Set<string> }
>()

for (const [baseStr, config] of Object.entries(payrollVariants)) {
  if (!config) continue

  const base = baseStr as unknown as PayrollSystem
  for (const [keysStr, composedValue] of Object.entries(config.compositions)) {
    variantLookup.set(composedValue, {
      base,
      activeKeys: new Set(keysStr.split(',')),
    })
  }
}

const variantPayrollSystems = new Set<string>(
  [...variantLookup.keys()].map((k) => k as unknown as string)
)

/** Returns payroll system options, excluding composed variant values */
export const getPayrollSystemOptions = () => {
  return Object.entries(PayrollSystem)
    .filter(([key]) => !variantPayrollSystems.has(key))
    .map(([key]) => {
      const payrollSystemMap = payrollSystemMapping[key]
      return {
        label: payrollSystemMap?.name,
        value: key,
      }
    })
}

/** Decomposes a stored payroll system value into its base system and active variant keys */
export const resolveVariant = (
  value: PayrollSystem | undefined
): { baseSystem: PayrollSystem | undefined; activeKeys: Set<string> } => {
  if (value == null) return { baseSystem: undefined, activeKeys: new Set() }

  const lookup = variantLookup.get(value)
  if (lookup) {
    return { baseSystem: lookup.base, activeKeys: new Set(lookup.activeKeys) }
  }
  return { baseSystem: value, activeKeys: new Set() }
}

/** Composes a payroll system value from base + active variant keys */
export const composeVariant = (
  base: PayrollSystem,
  activeKeys: Set<string>
): PayrollSystem => {
  if (activeKeys.size === 0) return base
  const keysStr = [...activeKeys].sort((a, b) => a.localeCompare(b)).join(',')
  return payrollVariants[base]?.compositions[keysStr] ?? base
}
